import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {AppState} from '../../../store/models/app-state.model';
import {Store} from '@ngrx/store';
import {PropertyNotesModel} from '../../models/properties/properties.models';
import * as fromProperty from '../../../store/reducers/property';
import {cloneDeep} from 'lodash';
import moment from 'moment';
import {NOTEABLE_TYPES} from '../../constants/noteableTypes.constants';
import {
  ISO_FROM_BACKEND, SIMPLE_TEXT_FORMAT, TIME_FORMAT
} from '../../../core/constants/date-and-time-formats.constants';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {PropertiesService} from '../../../core/http/properties/properties.service';
import {UsersService} from '../../../core/http/users/users.service';
import {SortArrayPipe} from '../../pipes/sort-array/sort-array.pipe';
import { PropertyNotesActions } from '../../../store/actions/property/property-notes.actions';


@Component({
  selector: 'app-notes',
  templateUrl: './notes.component.html',
  styleUrls: ['./notes.component.scss']
})
export class NotesComponent implements OnInit {
  propertyNotes: PropertyNotesModel[];
  sellerNotes: PropertyNotesModel[];
  propertyUserNotes: PropertyNotesModel[];
  isLoadingNotes = true;
  propertyId: number;
  filterSetToAll = true;
  allNotesCount = 0;
  filterSetToPropertyNotes = false;
  propertyNotesCount = 0;
  filterSetToSellerNotes = false;
  sellerNotesCount = 0;
  showAddNotesForm = false;
  notes: PropertyNotesModel[];
  starOff: boolean;
  sellerId: number;
  noteForm: UntypedFormGroup;

  constructor(
    private route: ActivatedRoute,
    private store: Store<AppState>,
    private formBuilder: UntypedFormBuilder,
    private propertyService: PropertiesService,
    private usersService: UsersService,
    private sortPipe: SortArrayPipe
  ) {
  }

  ngOnInit(): void {
    this.getPropertyId();
    this.getSellerIdAndLoadDetails();
    this.getPropertyNotes();
    this.createForm();
  }

  getPropertyId(): void {
    const id = Number(this.route.snapshot.paramMap.get('propertyId'));
    this.propertyId = !isNaN(id) ? id : null;

  }

  dispatchLoadPropertyNotes(): void {
    this.store.dispatch(PropertyNotesActions.loadPropertyNotes({
      propertyId: this.propertyId,
      userId: this.sellerId,
      params: {
        fields: [
          'id',
          'task',
          'property-id',
          'user-id',
          'note',
          'noteable-id',
          'noteable-type',
          'created-at',
          'updated-at',
          'photo-url',
          'event',
          'is-sticky',
          'salesforce-id',
          'salesforce-updated-at',
          'author',
          'name',
          'subject'
        ],
      }
    }));
  }

  getPropertyNotes(): void {
    this.store.select(fromProperty.selectPropertyState).subscribe((data) => {
      this.isLoadingNotes = data.propertyNotes.isLoading;
      this.propertyNotes = data.propertyNotes.details.propertyNotes;
      this.sellerNotes = data.propertyNotes.details.sellerNotes;
      this.propertyUserNotes = data.propertyNotes.details.propertyUserNotes;
      this.filterSetToAll = true;
      const propertyNotesNumber = !isNaN(this.propertyNotes?.length) ? this.propertyNotes.length : 0;
      const propertyUserNotesNumber = !isNaN(this.propertyUserNotes?.length) ? this.propertyUserNotes.length : 0;
      this.propertyNotesCount = propertyNotesNumber + propertyUserNotesNumber;
      this.sellerNotesCount = !isNaN(this.sellerNotes?.length) ? this.sellerNotes.length : 0;
      this.allNotesCount = this.propertyNotesCount + this.sellerNotesCount;
      this.selectNotes();
      this.sortNotes(this.notes);
    });
  }

  public selectNotes(): PropertyNotesModel[] {
    const propertyNotes = Array.isArray(this.propertyNotes) ? cloneDeep(this.propertyNotes) : [];
    const sellerNotes = Array.isArray(this.sellerNotes) ? cloneDeep(this.sellerNotes) : [];
    const propertyUserNotes = Array.isArray(this.propertyUserNotes) ? cloneDeep(this.propertyUserNotes) : [];
    this.notes = [
      ...propertyNotes,
      ...sellerNotes,
      ...propertyUserNotes
    ];
    return this.notes;
  }

  public setFilterToAll(): void {
    this.filterSetToAll = true;
    this.filterSetToPropertyNotes = false;
    this.filterSetToSellerNotes = false;
    this.selectNotes();
    this.sortNotes(this.notes);
  }

  public setFilterToPropertyNotes(): void {
    this.filterSetToPropertyNotes = true;
    this.filterSetToSellerNotes = false;
    this.filterSetToAll = false;
    this.notes = [
      ...this.propertyNotes,
      ...this.propertyUserNotes
    ];
    this.sortNotes(this.notes);
  }

  public setFilterToSellerNotes(): void {
    this.filterSetToSellerNotes = true;
    this.filterSetToPropertyNotes = false;
    this.filterSetToAll = false;
    this.notes = this.sellerNotes;
    this.sortNotes(this.notes);
  }

  public formatDate(note: PropertyNotesModel): string {
    const date = note.createdAt ? note.createdAt : note.created_at;
    return moment(date, ISO_FROM_BACKEND).format(SIMPLE_TEXT_FORMAT);
  }

  public formatTime(note: PropertyNotesModel): string {
    const time = note.createdAt ? note.createdAt : note.created_at;
    return moment(time, ISO_FROM_BACKEND).format(TIME_FORMAT);
  }

  public sortNotes(notes: PropertyNotesModel[]): void {
    const starredNotes = [];
    const otherNotes = [];
    for (const note of notes) {
      if (note.is_sticky === 1 || note.isSticky === true) {
        starredNotes.push(note);
      } else {
        otherNotes.push(note);
      }
    }

    const sortedStarredNotes = this.sortPipe.transform(starredNotes, 'desc', 'createdAt');
    const sortedOtherNotes = this.sortPipe.transform(otherNotes, 'desc', 'createdAt');
    this.notes = [
      ...sortedStarredNotes,
      ...sortedOtherNotes
    ];
  }

  public displayStrategy(note: PropertyNotesModel): string {
    if (note.noteableType || note.noteable_type) {
      const strategy = note.noteableType !== undefined ? note.noteableType : note.noteable_type;
      for (const type of NOTEABLE_TYPES) {
        if (type.value === strategy) {
          return type.label;
        }
      }
    }
  }

  private createForm(): void {
    this.noteForm = this.formBuilder.group({
      note: ['', [Validators.required]]
    });
  }

  public submit(): void {
    this.propertyService.createNote(this.propertyId, {
      note: this.noteForm.value.note
    }).subscribe(() => {
      this.getSellerIdAndLoadDetails();
      this.getPropertyNotes();
      this.showAddNotesForm = false;
      this.noteForm.reset();
    });
  }

  public updateStickyNote(note: PropertyNotesModel): void {

    const isSticky = note.isSticky !== undefined ? note.isSticky : note.is_sticky;
    let stickyUserValue;
    let stickyPropertyValue;


    switch (isSticky) {
      case 0:
        stickyUserValue = 1;
        break;
      case 1:
        stickyUserValue = 0;
        break;
      case false:
        stickyPropertyValue = true;
        break;
      case true:
        stickyPropertyValue = false;
        break;
      default:
        break;
    }
    if (note.isSticky !== undefined) {
      note.isSticky = stickyPropertyValue;
    } else {
      note.is_sticky = stickyUserValue;
    }

    this.determineNoteType(note);
  }

  public determineNoteType(note: PropertyNotesModel): void {
    if (note.subject) {
      if (typeof note.is_sticky === 'boolean' || typeof note.isSticky === 'boolean') {
        this.setPropertyUserNoteAsSticky(note);
      } else {
        this.setUserNoteAsSticky(note);
      }
    } else {
      this.setPropertyNoteAsSticky(note);
    }
  }

  public setUserNoteAsSticky(note: PropertyNotesModel): void {
    if (note.is_sticky !== undefined) {
      const isSticky = note.is_sticky;
      this.usersService.updateUserNote(note.id, isSticky).subscribe(() => {
        this.sortNotes(this.notes);
      });
    }
  }

  public setPropertyUserNoteAsSticky(note: PropertyNotesModel): void {
    if (note.isSticky !== undefined) {
      const sticky = note.isSticky;
      this.usersService.updateUserNote(note.id, sticky).subscribe(() => {
        this.sortNotes(this.notes);
      });
    }
  }

  public setPropertyNoteAsSticky(note: PropertyNotesModel): void {
    if (note.isSticky !== undefined) {
      const sticky = note.isSticky;
      this.propertyService.updatePropertyNote(note.id, sticky).subscribe(() => {
        this.sortNotes(this.notes);
      });
    }
  }

  public getSellerIdAndLoadDetails(): void {
    this.store.select(fromProperty.selectPropertyDetailsState).subscribe(data => {
      this.sellerId = data.details?.userId;

      if (this.sellerId) {
        this.dispatchLoadPropertyNotes();
      }
    });
  }

}
